import { useEffect, useState } from "react";
import { Link, NavLink, Route, Routes, useNavigate } from "react-router";
import { ToastProvider } from "../../components/Toast.js";
import { useT } from "../../i18n/LocaleProvider.js";
import { api, ApiError } from "../../lib/api.js";
import { useDocumentTitle } from "../../lib/useDocumentTitle.js";
import { AdminClips } from "./Clips.js";
import { AdminInbox } from "./Inbox.js";
import { AdminQueue } from "./Queue.js";
import { AdminSubmission } from "./Submission.js";

/**
 * The review queue. Behind the API's cookie session AND, in production,
 * Traefik basic-auth in front of /admin and /api/admin — two independent
 * credentials for the most sensitive data on the box.
 */
export function AdminApp() {
  const { m, lp } = useT();
  useDocumentTitle(m.admin.title);
  const [authed, setAuthed] = useState<boolean | null>(null);
  useEffect(() => {
    api.get("/api/admin/me").then(() => setAuthed(true)).catch(() => setAuthed(false));
  }, []);

  if (authed === null) return <div className="container admin"><p className="muted">{m.common.loading}…</p></div>;
  if (!authed) return <Login onLogin={() => setAuthed(true)} />;

  return (
    <ToastProvider>
      <div className="container admin">
        <div className="admin__bar">
          <h1>
            <Link to={lp("/admin")} style={{ color: "inherit", textDecoration: "none" }}>
              {m.admin.title}
            </Link>
          </h1>
          <nav className="admin__tabs" aria-label="Admin">
            <NavLink className="admin__tab" to={lp("/admin")} end>
              {m.admin.queue}
            </NavLink>
            <NavLink className="admin__tab" to={lp("/admin/inbox")}>
              {m.admin.contacts}
            </NavLink>
            <NavLink className="admin__tab" to={lp("/admin/clips")}>
              {m.admin.clips}
            </NavLink>
          </nav>
          <button
            type="button"
            className="btn btn--ghost btn--small"
            onClick={() => api.post("/api/admin/logout", {}).then(() => setAuthed(false))}
          >
            {m.admin.signOut}
          </button>
        </div>
        <Routes>
          <Route index element={<AdminQueue />} />
          <Route path="submissions/:id" element={<AdminSubmission />} />
          <Route path="inbox" element={<AdminInbox />} />
          <Route path="messages/:id" element={<AdminInbox />} />
          <Route path="support/:id" element={<AdminInbox />} />
          <Route path="clips" element={<AdminClips />} />
        </Routes>
      </div>
    </ToastProvider>
  );
}

function Login({ onLogin }: { onLogin: () => void }) {
  const { m } = useT();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <div className="container container--narrow admin">
      <form
        className="card form"
        style={{ maxWidth: 420 }}
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          setError(null);
          try {
            await api.post("/api/admin/login", { password });
            onLogin();
            navigate(".", { replace: true });
          } catch (err) {
            setError(err instanceof ApiError && err.status === 429 ? m.share.errors.rateLimited : m.common.error);
          } finally {
            setBusy(false);
          }
        }}
      >
        <h1 style={{ fontSize: "1.3rem", margin: 0 }}>{m.admin.title}</h1>
        <div className="field">
          <label className="field__label" htmlFor="pw">
            {m.admin.password}
          </label>
          <input id="pw" className="input" type="password" autoComplete="current-password" dir="ltr" value={password} onChange={(e) => setPassword(e.target.value)} required />
        </div>
        {error && (
          <p className="field__error" role="alert">
            {error}
          </p>
        )}
        <button className="btn btn--primary" type="submit" disabled={busy}>
          {m.admin.login}
        </button>
      </form>
    </div>
  );
}
